import { Component, Input, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { IUserProfile } from 'src/app/lego-utils';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { UserMaterialService } from 'src/app/services';
import { ResponsibleUserService } from 'src/app/state';

@Component({
  selector: 'em-add-edit-responsible-user-drawer',
  templateUrl: './add-or-edit-responsible-user-drawer.component.html',
  standalone: false,
})
export class AddOrEditResponsibleUserDrawerComponent implements OnDestroy {
  public isLoading = false;
  public readonly formGroup = this.formBuilder.group({
    // eslint-disable-next-line @typescript-eslint/unbound-method
    user: [undefined, Validators.required],
  });

  @Input() public designId?: string;

  private readonly subscriptions = new Subscription();

  public constructor(
    private readonly notification: NzNotificationService,
    private readonly drawerRef: NzDrawerRef,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly userMaterialService: UserMaterialService,
    private readonly responsibleUserService: ResponsibleUserService
  ) {}

  public submitForm(formGroup: UntypedFormGroup): void {
    this.isLoading = true;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const user: IUserProfile | undefined = formGroup.get('user')?.value;

    if (!user) {
      throw new Error('No user has been selected!');
    }

    if (!user.email) {
      throw new Error('This user has no email in SAP and therefore cannot be selected.');
    }

    const setUser$ = this.userMaterialService.getUserIdByEmail(user.email).pipe(
      switchMap((res) => {
        if (!res.userID) {
          throw new Error(
            'No username found in SAP for user selected. Please verify that username is present in SAP.'
          );
        }

        if (!this.designId) {
          throw new Error('No design idea found.');
        }

        return this.responsibleUserService.setResponsibleUser(res.userID, this.designId);
      })
    );

    this.subscriptions.add(
      setUser$.subscribe({
        next: () => {
          this.notification.success(
            'Responsible user set',
            'The Responsible user has been updated'
          );

          this.drawerRef.close();
        },
        error: (error: unknown) => {
          this.isLoading = false;
          const errorMessage = error instanceof Error ? error.message : String(error);
          this.notification.error('Error!', errorMessage);
        },
      })
    );
  }

  public cancel(): void {
    this.drawerRef.close();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
